import { deleteOrgEntityStatusesBySlugMutation, getOrgEntityStatusesQueryKey, patchOrgEntityStatusesBySlugMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoTrash16 } from '@onefootprint/icons';
import type { VaultKind } from '@onefootprint/request-types';
import { Dialog, useConfirmationDialog } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomStatusForm, { type CustomStatusFormData } from '../custom-status-form';
type CustomStatusEditDialogProps = {
  entityKind: VaultKind;
  open: boolean;
  onClose: () => void;
  defaultValues?: CustomStatusFormData;
};
const CustomStatusEditDialog = ({
  entityKind,
  open,
  onClose,
  defaultValues
}: CustomStatusEditDialogProps) => {
  const {
    t
  } = useTranslation('settings', {
    keyPrefix: 'pages.statuses.customStatusAddEditDialog'
  });
  const [isDirty, setIsDirty] = useState(false);
  const confirm = useConfirmationDialog();
  const editCustomStatusMutation = useMutation(patchOrgEntityStatusesBySlugMutation());
  const deleteCustomStatusMutation = useMutation(deleteOrgEntityStatusesBySlugMutation());
  const showRequestErrorToast = useRequestErrorToast();
  const queryClient = useQueryClient();
  const handleSave = async (data: CustomStatusFormData) => {
    try {
      await editCustomStatusMutation.mutateAsync({
        path: {
          slug: data.slug
        },
        body: {
          displayName: data.displayName,
          entityType: data.entityKind,
          entries: data.entries.map(entry => ({
            displayName: entry.displayName,
            slug: entry.slug,
            hexCode: entry.hexCode
          }))
        }
      });
      queryClient.invalidateQueries({
        queryKey: getOrgEntityStatusesQueryKey()
      });
      onClose();
    } catch (error) {
      showRequestErrorToast(error);
    }
  };
  const handleDelete = async () => {
    try {
      if (defaultValues?.slug) {
        await deleteCustomStatusMutation.mutateAsync({
          path: {
            slug: defaultValues?.slug
          }
        });
        queryClient.invalidateQueries({
          queryKey: getOrgEntityStatusesQueryKey()
        });
        onClose();
      }
    } catch (error_0) {
      showRequestErrorToast(error_0);
    }
  };
  const handleDeleteClick = () => {
    confirm.open({
      title: t($ => $.form.deleteConfirmation.title),
      description: t($_0 => $_0.form.deleteConfirmation.description, {
        displayName: defaultValues?.displayName
      }),
      primaryButton: {
        label: t($_1 => $_1.form.delete),
        onClick: handleDelete
      },
      secondaryButton: {
        label: t($_2 => $_2.form.cancel)
      }
    });
  };
  const handleClose = () => {
    if (!isDirty) {
      onClose();
      return;
    }
    confirm.open({
      title: t($_3 => $_3.form.closeConfirmation.title),
      description: t($_4 => $_4.form.closeConfirmation.description),
      primaryButton: {
        label: t($_5 => $_5.form.discardChanges),
        onClick: onClose
      },
      secondaryButton: {
        label: t($_6 => $_6.form.cancel)
      }
    });
  };
  return <Dialog open={open} onClose={handleClose} title={t($_7 => $_7.title.edit)} linkButton={{
    label: t($_8 => $_8.form.delete),
    destructive: true,
    iconComponent: IcoTrash16,
    iconPosition: 'left',
    onClick: handleDeleteClick
  }} primaryButton={{
    label: t($_9 => $_9.form.save),
    type: 'submit',
    form: 'custom-status-add-edit-form',
    loading: editCustomStatusMutation.isPending
  }} secondaryButton={{
    label: t($_10 => $_10.form.cancel),
    onClick: handleClose
  }}>
      <CustomStatusForm kind="edit" entityKind={entityKind} defaultValues={defaultValues} onSubmit={handleSave} setDirty={setIsDirty} />
    </Dialog>;
};
export default CustomStatusEditDialog;